/**
 * Shared bits for the Autobuild console (CDID / AHC / APD).
 * Times render in the viewer's timezone, always with a zone abbreviation so
 * they are never ambiguous. Session stamps stay UTC — they are identifiers,
 * not clock readings. Log rows carry their UTC instant in a hover title so
 * they still correlate with Slack and the BigQuery flight recorder.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  AutobuildActivityComponent,
  AutobuildKind,
  AutobuildOutcome,
  AutobuildSessionSummary,
  CdidLifecycleState,
} from '@a7y/types';
import { api } from '@/lib/api';

/**
 * Tab selection driven by the URL hash, so a tab is linkable and survives a
 * reload: `/admin/automation/cdid#tickets` opens Tickets, a bare URL opens the
 * first tab, and an unrecognised hash falls back to the first rather than
 * rendering nothing.
 *
 * The hash is the single source of truth — the active tab is *derived* from it
 * rather than mirrored into `useState`. That keeps browser back/forward and
 * hand-edited URLs working for free; a second copy in component state would
 * have to be synced back and could disagree with the address bar.
 *
 * Selecting a tab uses `replace`, not `push`: pressing Back after clicking
 * through three tabs should leave the page, not walk back through them.
 */
export function useHashTab<T extends string>(tabIds: readonly T[]): [T, (id: T) => void] {
  const location = useLocation();
  const navigate = useNavigate();

  const raw = decodeURIComponent(location.hash.replace(/^#/, '')).toLowerCase();
  const active = (tabIds as readonly string[]).includes(raw) ? (raw as T) : tabIds[0];

  const selectTab = useCallback(
    (id: T) => {
      // Preserve pathname and query — a bare `{ hash }` would drop any search
      // params the page is using.
      navigate(
        { pathname: location.pathname, search: location.search, hash: `#${id}` },
        { replace: true }
      );
    },
    [navigate, location.pathname, location.search]
  );

  return [active, selectTab];
}

/**
 * Newest ticket first, by the numeric part of the identifier — `A7Y-2167`
 * before `A7Y-987`. String compare would order those wrongly (shorter numbers
 * sort late), so the digits are compared as numbers.
 *
 * Returns a new array; never mutates the caller's (these come straight from a
 * react-query cache, and sorting in place would corrupt it).
 *
 * Display-only. The CDID queue endpoint still returns `eligible` in the
 * session's own selection order — priority, then oldest first — and that
 * ordering, not this one, is what governs dispatch.
 */
export function sortByIdDesc<T extends { identifier?: string; id?: string }>(
  rows: readonly T[]
): T[] {
  // Match the TRAILING digit run. Stripping a leading non-digit prefix instead
  // (`/^\D+/`) silently fails on this team's key: `\D` stops at the `7` in
  // "A7Y", leaving "7Y-2167" → parseInt → 7 for every ticket, so the sort
  // becomes a no-op that looks like it worked.
  const num = (row: T) => {
    const raw = row.identifier ?? row.id ?? '';
    const m = /(\d+)\s*$/.exec(raw);
    return m ? Number.parseInt(m[1], 10) : -1;
  };
  return [...rows].sort((a, b) => num(b) - num(a));
}

/** 1s-tick clock for live countdown / elapsed displays. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function fmtUtc(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/**
 * The viewer's zone abbreviation — "CEST", "CET". Empty string when the
 * platform declines to supply one, which every caller treats as "omit the
 * suffix" rather than guessing.
 *
 * THE LOCALE IS PINNED TO `en-GB`, and that is what produces a NAME rather than
 * an offset (TPF, 2026-09-09). ICU's short zone abbreviations are locale data,
 * not zone data: with the browser's default locale — `en-US` in Chrome here —
 * `Europe/Amsterdam` renders `GMT+2`, while `en-GB` renders `CEST`. Measured
 * across four zones on 2026-09-09.
 *
 * The trade is real and is accepted rather than hidden: `en-GB` gives `GMT-4`
 * for `America/New_York` where `en-US` would give `EDT`. Both readers of this
 * console are in Europe, and `en-GB` is already the pinned locale for every
 * date this app formats (`audit-spend/format.ts`, `CdidFlowSection`), so one
 * locale for all of it beats two behaviours that disagree.
 *
 * The zone itself still comes from the browser: only the LANGUAGE is pinned,
 * never `timeZone`.
 */
export function localZoneAbbrev(d: Date = new Date()): string {
  return (
    new Intl.DateTimeFormat('en-GB', { timeZoneName: 'short' })
      .formatToParts(d)
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * Same shape as `fmtUtc` but in the viewer's timezone, with the zone
 * abbreviation so it is never ambiguous — "2026-07-27 16:32 EDT". Used for
 * session start times, which people read as "when did this happen to me".
 * Session stamps stay UTC — they are identifiers, and rewriting one would
 * stop it matching the run it names. Log rows moved to local on 2026-08-02
 * (TPF) and carry their UTC instant in a hover title instead.
 */
export function fmtLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  const tz = localZoneAbbrev(d);
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return tz ? `${stamp} ${tz}` : stamp;
}

/**
 * Finish time in the viewer's timezone. Drops the date when the session ended
 * on the same local day it started — the common case, and repeating the date
 * beside the start column is noise. A session that crossed local midnight gets
 * a `+1d` day-offset suffix instead, so "ended before it started" can never be
 * implied: it used to repeat the whole `YYYY-MM-DD HH:MM TZ` stamp, which made
 * every row's column three times wider than the case it was serving.
 */
export function fmtLocalEnd(startedAt: string, endedAt: string): string {
  const start = new Date(startedAt);
  const end = new Date(endedAt);
  const sameLocalDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  const tz = localZoneAbbrev(end);
  const hm = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
  const clock = tz ? `${hm} ${tz}` : hm;
  if (sameLocalDay) return clock;
  // Whole local days between the two dates, so a session spanning a weekend
  // reads `+3d` rather than implying it finished the morning after.
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(end) - midnight(start)) / 86_400_000);
  return `${clock} +${days}d`;
}

/**
 * The local calendar day of an instant as `YYYY-MM-DD` — the exact value an
 * `<input type="date">` reads and writes.
 *
 * Built from the LOCAL getters, never `toISOString().slice(0, 10)`: that is the
 * UTC date, which is the previous day for a European viewer for the first two
 * hours of every morning.
 */
export function localDayValue(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The half-open UTC instant window `[from, to)` covering one local calendar day.
 *
 * The picker is the only place the viewer's zone is known, so it is where the
 * conversion happens — the API takes instants and is never told a date. `new
 * Date(y, m, d)` is local midnight by definition, and passing `d + 1` rolls the
 * month and year without any wrap arithmetic here.
 */
export function localDayWindow(day: string): { from: string; to: string } {
  const [year, month, date] = day.split('-').map(Number);
  return {
    from: new Date(year, month - 1, date).toISOString(),
    to: new Date(year, month - 1, date + 1).toISOString(),
  };
}

/** "2h 14m" / "14m 05s" / "42s" */
export function fmtDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

/** "3h 07m ago", or "—" for a null instant — never invented from silence. */
export function relativeAgo(iso: string | null, nowMs: number): string {
  if (iso == null) return '—';
  return `${fmtDuration(Math.max(0, nowMs - Date.parse(iso)) / 1000)} ago`;
}

/**
 * "2h10m" — hours then zero-padded minutes, no space and no seconds. A
 * BUDGET figure read at a glance against another budget figure ("2h10m today
 * · avg 3h05m" on the CDID health strip, or a cycle row's own worker time),
 * not a precise elapsed-time reading the way `fmtDuration`'s "2h 05m" is —
 * the compact, unspaced pairing is what makes two of them scan as one
 * comparison.
 */
export function fmtHm(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}m`;
  return `${m}m`;
}

/** Countdown to a future ISO time; clamps at "due" once passed. */
export function fmtCountdown(iso: string, nowMs: number): string {
  const delta = Date.parse(iso) - nowMs;
  if (delta <= 0) return 'due — awaiting GitHub trigger';
  return `in ${fmtDuration(delta / 1000)}`;
}

export const OUTCOME_STYLES: Record<
  AutobuildOutcome,
  { dot: string; badge: string; label: string }
> = {
  success: {
    dot: 'bg-emerald-500',
    badge:
      'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
    label: 'Success',
  },
  failure: {
    dot: 'bg-red-500',
    badge:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
    label: 'Failed',
  },
  running: {
    dot: 'bg-amber-400 animate-pulse',
    badge:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
    label: 'Running',
  },
  // Grey, deliberately not red: a skip is the system declining to act for a
  // stated reason, which is correct behaviour — not a failure.
  skipped: {
    dot: 'bg-zinc-400',
    badge:
      'bg-zinc-100 text-zinc-600 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700',
    label: 'Skipped',
  },
};

/** Live duration for a session: fixed when ended, ticking elapsed when running. */
export function sessionDurationSeconds(session: AutobuildSessionSummary, nowMs: number): number {
  if (session.durationSeconds != null) return session.durationSeconds;
  return (nowMs - Date.parse(session.startedAt)) / 1000;
}

/**
 * How long a CYCLE took: its own start to the last finish among itself and
 * everything it dispatched.
 *
 * A dispatcher's own lifetime is not the cycle's. An ingress attempt selects,
 * arms workers and exits in ~43 seconds while the workers it started run for
 * minutes to hours — so a row reporting the dispatcher's duration says a cycle
 * that ran for an hour took under a minute, and the page reads as though
 * nothing takes any time. Measured 2026-09-15: 19 worker runs spanning 4m 39s
 * to 1h 16m sat under parents reporting seconds.
 *
 * A row with no children is its own cycle and this is exactly
 * `sessionDurationSeconds`. A child still running keeps the cycle running —
 * `nowMs` is the open end — which is why the caller must pass the same clock it
 * renders with.
 */
export function cycleDurationSeconds(
  session: AutobuildSessionSummary,
  children: readonly AutobuildSessionSummary[],
  nowMs: number
): number {
  const startMs = Date.parse(session.startedAt);
  const endOf = (s: AutobuildSessionSummary): number =>
    s.endedAt ? Date.parse(s.endedAt) : nowMs;
  const lastEnd = [session, ...children].reduce((latest, s) => Math.max(latest, endOf(s)), startMs);
  return Math.max(0, (lastEnd - startMs) / 1000);
}

/**
 * Operator-facing labels for `CdidLifecycleState` — moved out of
 * `SessionThroughputSection.tsx` (A7Y-3962 T4) so `StageChips` and the
 * session-detail per-ticket chips share the one set rather than each growing
 * their own. `green` reads "Waiting to land": D5's whole point is that a
 * green ticket has passed review and is sitting on a merge the lander has not
 * gotten to yet, and "Green" alone does not say that.
 */
export const LIFECYCLE_STATE_LABELS: Record<CdidLifecycleState, string> = {
  queued: 'Queued',
  planning: 'Planning',
  implementing: 'Active',
  in_review: 'Review',
  fix_in_flight: 'Fix',
  green: 'Waiting to land',
  landing: 'Landing',
  landed: 'Landed',
  blocked: 'Blocked',
  parked: 'Parked',
  deferred: 'Deferred',
};

/** Moved out of `SessionThroughputSection.tsx` verbatim (A7Y-3962 T4). */
export const LIFECYCLE_STATE_STYLES: Record<CdidLifecycleState, string> = {
  queued: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  planning: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  implementing: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300',
  in_review: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
  fix_in_flight: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  landing: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300',
  landed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  parked: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  deferred: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
};

/**
 * Hover-title explanations for each stage — several of these ("fix", "green")
 * are not self-explanatory the way "Landed" is, matching the pattern
 * `TICKET_OUTCOMES` already uses on the session-detail page.
 */
export const LIFECYCLE_STATE_HINTS: Record<CdidLifecycleState, string> = {
  queued: 'Claimed or admitted, waiting for an implementation slot',
  planning: 'Scoped before implementation starts',
  implementing: 'An implementation slot is actively building this work',
  in_review: 'Implementation finished; Integration/Karen review is active',
  fix_in_flight: 'A review fix is actively being applied',
  green: 'Passed review and is waiting for the lander to merge it',
  landing: 'The lander is merging this to the release branch now',
  landed: 'Merged to the release branch',
  blocked: 'Blocked — could not proceed',
  parked: 'Stopped and handed to the human queue',
  deferred: 'Eligible but not taken this run — carried to a later session',
};

export const KIND_LABELS: Record<string, string> = {
  cdid: 'CDID',
  ahc: 'AHC',
  apd: 'APD',
};

/** Operator-facing labels for the physical component behind an Activity row. */
export const ACTIVITY_COMPONENT_LABELS: Record<AutobuildActivityComponent, string> = {
  'implementation-session': 'Implementation session',
  worker: 'Worker',
  'ingress-scheduler': 'Ingress scheduler',
  // A7Y-3962. The two layers the console could not see. The lander is where
  // `landed` is written, which is why every CDID run read "0 resolved" until
  // its kind entered the query.
  'hot-lane': 'Hot lane',
  lander: 'Lander',
  'legacy-unclassified': 'Legacy CDID — component not recorded',
};

/**
 * Scheduled session slots plus APD coordinator backstops, mirrored from the
 * corresponding workflow files.
 *
 * **Keep this in sync when a workflow's cron changes.** There is no runtime
 * link between the two, so drift is silent and the console starts reporting
 * slots that do not exist. Observed 2026-07-30: CDID moved to `37 * /6` while
 * this table still said `0 * /4`, so the page called the session "overdue" at
 * 16:00 — a slot that had been removed hours earlier.
 *
 * `everyHours` = interval cron (fires at that minute past every Nth hour);
 * `hour` = daily cron at a fixed hour; `hours` = several daily backstops.
 */
const CRON: Record<
  AutobuildKind,
  { everyHours?: number; hour?: number; hours?: number[]; minute: number }
> = {
  cdid: { everyHours: 6, minute: 37 }, // 37 */6 * * *  -> 00:37 06:37 12:37 18:37
  ahc: { hour: 23, minute: 0 }, //         0 23 * * *
  apd: { hour: 0, minute: 23 }, //         23 0 * * *  (one daily cron; moved from 03:23 UTC 2026-09-21)
};

/**
 * Next cron slot (UTC) for a routine, strictly after `nowMs`. GitHub fires well
 * after the slot (see SCHEDULER_GRACE_MS), so this is "next scheduled", not
 * "next actual".
 */
export function nextSlotMs(kind: AutobuildKind, nowMs: number): number {
  const c = CRON[kind];
  const next = new Date(nowMs);
  next.setUTCSeconds(0, 0);
  if (c.everyHours != null) {
    // This period's slot; roll to the next period when it has already passed.
    // setUTCHours accepts >23 and rolls the date over, so no manual wrap.
    const periodStart = Math.floor(next.getUTCHours() / c.everyHours) * c.everyHours;
    next.setUTCHours(periodStart, c.minute, 0, 0);
    if (next.getTime() <= nowMs) next.setUTCHours(periodStart + c.everyHours, c.minute, 0, 0);
    return next.getTime();
  }
  if (c.hours != null) {
    for (const hour of c.hours) {
      next.setUTCHours(hour, c.minute, 0, 0);
      if (next.getTime() > nowMs) return next.getTime();
    }
    next.setUTCDate(next.getUTCDate() + 1);
    next.setUTCHours(c.hours[0], c.minute, 0, 0);
    return next.getTime();
  }
  next.setUTCHours(c.hour as number, c.minute, 0, 0);
  if (next.getTime() <= nowMs) next.setUTCDate(next.getUTCDate() + 1);
  return next.getTime();
}

/** "3:07" — hours:minutes remaining, for the routine-page subheaders. */
export function fmtHoursMinutes(deltaMs: number): string {
  const totalMinutes = Math.max(0, Math.floor(deltaMs / 60_000));
  return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

/** Gap between consecutive slots, derived from CRON so it cannot drift from it. */
function slotPeriodMs(kind: AutobuildKind): number {
  const c = CRON[kind];
  return (c.everyHours ?? 24) * 3_600_000;
}

/**
 * Most recent cron slot at or before `nowMs`. Derived from `nextSlotMs` minus
 * one period rather than duplicating the per-kind slot arithmetic, so the two
 * can never disagree about where the boundaries are.
 */
export function prevSlotMs(kind: AutobuildKind, nowMs: number): number {
  const c = CRON[kind];
  if (c.hours != null) {
    const previous = new Date(nowMs);
    previous.setUTCSeconds(0, 0);
    for (const hour of [...c.hours].reverse()) {
      previous.setUTCHours(hour, c.minute, 0, 0);
      if (previous.getTime() <= nowMs) return previous.getTime();
    }
    previous.setUTCDate(previous.getUTCDate() - 1);
    previous.setUTCHours(c.hours[c.hours.length - 1], c.minute, 0, 0);
    return previous.getTime();
  }
  return nextSlotMs(kind, nowMs) - slotPeriodMs(kind);
}

/**
 * How long after a slot GitHub is still considered merely late rather than
 * broken. GitHub cron starts have historically lagged 29–79 minutes here.
 * CDID now uses an off-minute slot and APD is event-driven with two scheduled
 * backstops, but the same 90-minute display grace keeps scheduled CDID/AHC
 * status from declaring a missed run prematurely.
 */
const SCHEDULER_GRACE_MS = 90 * 60_000;

/**
 * The schedule line for a routine's page subheader.
 *
 * A plain countdown to the next slot is actively misleading in the window
 * after a slot passes but before GitHub fires it: the countdown rolls over to
 * the *following* slot, so the page reads "next run in 23:31" while a run is
 * imminent — indistinguishable from "today's run already happened, nothing to
 * see". A pending run and a run that never happened must not look the same.
 */
export function scheduleStatus(
  kind: AutobuildKind,
  nowMs: number,
  latest: { startedAt: string; outcome: AutobuildOutcome } | null
): string {
  if (kind === 'apd') {
    if (latest?.outcome === 'running') return 'Immutable candidate deployment in progress.';
    return `Event-driven from green staging candidates; next coordinator backstop in ${fmtHoursMinutes(nextSlotMs(kind, nowMs) - nowMs)}.`;
  }
  const prev = prevSlotMs(kind, nowMs);
  const latestStartMs = latest ? Date.parse(latest.startedAt) : null;
  const slotHasRun = latestStartMs != null && latestStartMs >= prev;

  if (!slotHasRun) {
    const slot = new Date(prev);
    const label = `${String(slot.getUTCHours()).padStart(2, '0')}:${String(slot.getUTCMinutes()).padStart(2, '0')}`;
    const lateMs = nowMs - prev;
    if (latest?.outcome === 'running') {
      return `${label} slot queued behind the session still running.`;
    }
    return lateMs <= SCHEDULER_GRACE_MS
      ? `${label} slot due — awaiting the GitHub scheduler, which typically fires 30–80 min late (${Math.round(lateMs / 60_000)}m so far).`
      : `${label} slot overdue — ${fmtHoursMinutes(lateMs)} since the slot with no run.`;
  }
  return `Next run in ${fmtHoursMinutes(nextSlotMs(kind, nowMs) - nowMs)}`;
}

/**
 * Live schedule line for a routine's subheader. Uses the same query key as
 * `RunHistory`, so the page shares that cache instead of issuing a second
 * request for data already on screen.
 */
export function useScheduleStatus(kind: AutobuildKind): string {
  const now = useNow();
  const history = useQuery({
    queryKey: ['autobuild-history', kind],
    queryFn: () => api.getAutobuildHistory(kind),
    refetchInterval: 60_000,
  });
  return scheduleStatus(kind, now, history.data?.sessions?.[0] ?? null);
}

/** Linear priority chip styling. Shared so the CDID queue tab and the session
 *  detail ticket list cannot drift into two different colour scales for the
 *  same four values. */
export const PRIORITY_STYLES: Record<string, string> = {
  Urgent: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  High: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  Medium: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  Low: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
};

/** A ticket carrying its Linear parent, as the CDID and APD queues return it. */
export interface ParentedTicket {
  identifier: string;
  parentIdentifier?: string | null;
  parentTitle?: string | null;
}

/** One parent's worth of queue tickets, ready to render as a titled group. */
export interface TicketGroup<T> {
  /** Parent identifier, or null for the top-level "No Parent" group. */
  parentIdentifier: string | null;
  /** Heading text: "No Parent", or "A7Y-2299 — <parent title>". */
  label: string;
  tickets: T[];
}

/**
 * Group queue tickets by parent for display. Shared by the CDID and APD tabs so
 * the two queues cannot drift into different grouping or heading formats for
 * what is the same idea (TPF, 2026-08-03).
 *
 * "No Parent" always sorts first — top-level work is what a reader scans for.
 * Remaining groups are ordered by parent id descending (newest epic first),
 * matching `sortByIdDesc` elsewhere on these pages. **Order within each group is
 * preserved exactly** as the caller supplied it, because for the CDID queue that
 * order is the session's own selection order (priority, then oldest first) and
 * re-sorting it here would misrepresent what runs next.
 *
 * A parent with no title falls back to the bare identifier rather than rendering
 * a dangling separator.
 */
export function groupByParent<T extends ParentedTicket>(tickets: T[]): TicketGroup<T>[] {
  const groups = new Map<string | null, TicketGroup<T>>();

  for (const t of tickets) {
    const key = t.parentIdentifier ?? null;
    let group = groups.get(key);
    if (!group) {
      group = {
        parentIdentifier: key,
        label: key === null ? 'No Parent' : t.parentTitle ? `${key} — ${t.parentTitle}` : key,
        tickets: [],
      };
      groups.set(key, group);
    }
    group.tickets.push(t);
  }

  const noParent = groups.get(null);
  const parented = [...groups.values()]
    .filter((g) => g.parentIdentifier !== null)
    .sort((a, b) => {
      const an = Number.parseInt((a.parentIdentifier ?? '').replace(/\D/g, ''), 10);
      const bn = Number.parseInt((b.parentIdentifier ?? '').replace(/\D/g, ''), 10);
      if (Number.isNaN(an) || Number.isNaN(bn)) {
        return (b.parentIdentifier ?? '').localeCompare(a.parentIdentifier ?? '');
      }
      return bn - an;
    });

  return noParent ? [noParent, ...parented] : parented;
}
